import React, { useState } from 'react';
import { Alert, Row, Col, Spinner } from 'react-bootstrap';
import { Styles } from '../styles/commentForm.js';
import { BLOG_COMMENT_ENDPOINT } from '../../../config/api';

const initialForm = {
    name: '',
    email: '',
    comment: ''
};

function CommentForm({ slug, onSubmitted }) {
    const [formValues, setFormValues] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState('');

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormValues((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
        setSubmitError('');
        setSubmitSuccess('');
    };

    const validate = () => {
        const validationErrors = {};
        if (!formValues.comment.trim()) {
            validationErrors.comment = "Comment can't be blank";
        }
        if (!formValues.name.trim()) {
            validationErrors.name = "Name can't be blank";
        }
        if (!formValues.email.trim()) {
            validationErrors.email = "Email can't be blank";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email.trim())) {
            validationErrors.email = 'Not a valid email';
        }
        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!slug) {
            setSubmitError('Unable to identify blog.');
            return;
        }
        if (!validate()) {
            return;
        }
        setSubmitting(true);
        setSubmitError('');
        setSubmitSuccess('');

        try {
            const endpoint = BLOG_COMMENT_ENDPOINT(slug);
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: formValues.name.trim(),
                    email: formValues.email.trim(),
                    comment: formValues.comment.trim()
                })
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Unable to post comment.');
            }

            setSubmitSuccess('Thank you! Your comment has been submitted.');
            setFormValues(initialForm);
            setErrors({});
            if (typeof onSubmitted === 'function') {
                onSubmitted();
            }
        } catch (err) {
            setSubmitError(err.message || 'Unable to post comment.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Styles>
            <div className="blog-comment-form">
                <h5>Leave a message here</h5>
                {submitError && <Alert variant="danger">{submitError}</Alert>}
                {submitSuccess && <Alert variant="success">{submitSuccess}</Alert>}
                <form className="form" onSubmit={handleSubmit} noValidate>
                    <Row>
                        <Col md="12">
                            <p className={`form-control ${errors.comment ? 'error' : ''}`}>
                                <textarea
                                    name="comment"
                                    placeholder="Enter your comment"
                                    value={formValues.comment}
                                    onChange={handleChange}
                                    disabled={submitting}
                                />
                                {errors.comment && <span className="comment_form-input-msg">{errors.comment}</span>}
                            </p>
                        </Col>
                        <Col md="6">
                            <p className={`form-control ${errors.name ? 'error' : ''}`}>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Enter your name"
                                    value={formValues.name}
                                    onChange={handleChange}
                                    disabled={submitting}
                                />
                                {errors.name && <span className="comment_form-input-msg">{errors.name}</span>}
                            </p>
                        </Col>
                        <Col md="6">
                            <p className={`form-control ${errors.email ? 'error' : ''}`}>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Enter your email"
                                    value={formValues.email}
                                    onChange={handleChange}
                                    disabled={submitting}
                                />
                                {errors.email && <span className="comment_form-input-msg">{errors.email}</span>}
                            </p>
                        </Col>
                        <Col md="12">
                            <button type="submit" disabled={submitting}>
                                {submitting ? <Spinner animation="border" size="sm" /> : 'Post Comment'}
                            </button>
                        </Col>
                    </Row>
                </form>
            </div>
        </Styles>
    );
}

export default CommentForm;
